import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Building2,
  RefreshCw,
  Plus,
  Search,
  Filter,
  TrendingUp,
  Globe,
  Map as MapIcon
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { usePOCManagement } from '@/hooks/admin/usePOCManagement';
import { POCTable } from './POCTable';
import { AmplifyPOCTable } from './AmplifyPOCTable';
import { POCStatisticsCards } from './POCStatisticsCards';
import { CreatePOCDialog } from './CreatePOCDialog';
import { CreateAmplifyPOCDialog } from './CreateAmplifyPOCDialog';

/**
 * POC Management Component - Main container for POC and Amplify POC management
 */
export function POCManagement() {
  const {
    pocs,
    amplifyPOCs,
    statistics,
    totalPOCs,
    totalAmplifyPOCs,
    pocFilters,
    amplifyFilters,
    isLoading,
    isStatisticsLoading,
    error,
    loadStatistics,
    loadPOCs,
    loadAmplifyPOCs,
    updatePOCFilters,
    updateAmplifyFilters,
    refreshData,
    clearError
  } = usePOCManagement();

  const [activeTab, setActiveTab] = useState('pocs'); // 'pocs' or 'amplify'
  const [showCreatePOC, setShowCreatePOC] = useState(false);
  const [showCreateAmplify, setShowCreateAmplify] = useState(false);

  // Load initial data
  useEffect(() => {
    loadStatistics();
    loadPOCs();
    loadAmplifyPOCs();
  }, []);

  // Reload data when filters change
  useEffect(() => {
    if (activeTab === 'pocs') {
      loadPOCs();
    }
  }, [pocFilters]);

  useEffect(() => {
    if (activeTab === 'amplify') {
      loadAmplifyPOCs();
    }
  }, [amplifyFilters]);

  const handleRefresh = async () => {
    await refreshData();
  };

  const tabs = [
    {
      id: 'pocs',
      label: 'All POCs',
      icon: Building2,
      count: totalPOCs
    },
    {
      id: 'amplify',
      label: 'Amplify POCs',
      icon: TrendingUp,
      count: totalAmplifyPOCs
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900  flex items-center gap-2">
            <MapPin className="h-7 w-7 text-gray-600 dark:text-gray-400" />
            POC Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage Point of Contact locations and Amplify POC budgets
          </p>
        </div>
        
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  {error.message}
                </p>
                {error.context && (
                  <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                    Context: {error.context}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={clearError}
              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <POCStatisticsCards 
        statistics={statistics} 
        isLoading={isStatisticsLoading} 
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-6 py-3 font-medium text-sm rounded-t-lg transition-colors
                  ${activeTab === tab.id
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-t-2 border-x border-gray-300 dark:border-gray-600 border-b-0'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }
                `}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`
                    px-2 py-0.5 rounded-full text-xs font-semibold
                    ${activeTab === tab.id
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }
                  `}>
                    {tab.count.toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          {/* POCs Tab */}
          {activeTab === 'pocs' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900  flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  All POC Locations
                </h3>
                <button
                  onClick={() => setShowCreatePOC(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm font-medium">Add POC</span>
                </button>
              </div>

              <POCTable
                pocs={pocs}
                totalPOCs={totalPOCs}
                filters={pocFilters}
                isLoading={isLoading}
                onFilterChange={updatePOCFilters}
                onRefresh={loadPOCs}
              />
            </div>
          )}

          {/* Amplify POCs Tab */}
          {activeTab === 'amplify' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  Amplify POC Locations
                </h3>
                <button
                  onClick={() => setShowCreateAmplify(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm font-medium">Add Amplify POC</span>
                </button>
              </div>

              <AmplifyPOCTable
                pocs={amplifyPOCs}
                totalPOCs={totalAmplifyPOCs}
                filters={amplifyFilters}
                isLoading={isLoading}
                onFilterChange={updateAmplifyFilters}
                onRefresh={loadAmplifyPOCs}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create POC Dialog */}
      {showCreatePOC && (
        <CreatePOCDialog
          isOpen={showCreatePOC}
          onClose={() => setShowCreatePOC(false)}
          onSuccess={() => {
            setShowCreatePOC(false);
            refreshData();
          }}
        />
      )}

      {/* Create Amplify POC Dialog */}
      {showCreateAmplify && (
        <CreateAmplifyPOCDialog
          isOpen={showCreateAmplify}
          onClose={() => setShowCreateAmplify(false)}
          onSuccess={() => {
            setShowCreateAmplify(false);
            refreshData();
          }}
        />
      )}
    </div>
  );
}

export default POCManagement;
